<script setup lang="ts">
import type { Deal } from '~/types/api'

/** Heart = follow the deal. */
const props = defineProps<{ deal: Deal }>()
const emit = defineEmits<{ change: [deal: Deal] }>()

const { $api } = useApi()
const requireLogin = useRequireLogin()
const followed = ref(props.deal.is_followed)
const busy = ref(false)

watch(() => props.deal.is_followed, (v) => (followed.value = v))

async function toggle() {
  if (!(await requireLogin())) return
  busy.value = true
  try {
    const updated = await $api<Deal>(`/api/deals/${props.deal.id}/follow`, {
      method: followed.value ? 'DELETE' : 'PUT',
    })
    followed.value = updated.is_followed
    emit('change', updated)
  } finally {
    busy.value = false
  }
}
</script>

<template>
  <button
    type="button"
    class="follow"
    :class="{ 'follow--on': followed }"
    :aria-pressed="followed"
    :aria-label="followed ? 'Unfollow deal' : 'Follow deal'"
    :disabled="busy"
    @click.prevent.stop="toggle"
  >
    <AppIcon name="heart" :size="18" />
  </button>
</template>

<style scoped>
.follow {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: 0;
  border-radius: 50%;
  background: var(--brand-700);
  color: var(--white);
}

.follow--on :deep(svg) {
  fill: currentColor;
}
</style>
